import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { IMobileMenuItem, IModule } from '@predictiveindex/tapestry';
import { ClassificationType } from '../../../core-data-model';
import {
    FeatureFlags,
    FeatureFlagsService,
    ModuleAccessService,
    ModuleUserAccessService
} from '../../../system-services';
import { TranslationService } from '../../../translations';
import { GlobalNavMenuActions, GlobalNavService } from '../global-nav.service';
@Component({
    selector: 'mobile-nav',
    template: `
        <tapestry-icon-button
            iconName="navigation_menu_bold"
            [altText]="'globalnav.myProfile-altText' | translate"
            trackingName="mobileNavigationTrigger"
            piTesting="mobileNavigationTrigger"
            [matMenuTriggerFor]="mobileMenu"
            #matMenuTriggerClick="matMenuTrigger"
        ></tapestry-icon-button>
        <mat-menu #mobileMenu="matMenu" class="mobile-menu--container">
            @if (modules.length > 0) {
                @for (module of modules; track module) {
                    <button
                        mat-menu-item
                        (click)="onSelect(module.moduleName)"
                        class="module-tabs--mobile"
                        [ngClass]="{
                            'module-tabs--mobile--active': isActiveModule(
                                module.untranslatedModuleName
                            )
                        }"
                        [attr.data-tracking]="module.trackingName"
                    >
                        {{ module.moduleName }}
                    </button>
                }
            }
            <mat-expansion-panel
                hideToggle
                (click)="$event.stopPropagation()"
                class="mobile-menu-items"
            >
                <mat-expansion-panel-header class="header--padding">
                    <mat-panel-title>
                        <tapestry-icon
                            class="mobile-menu-item--icon"
                            [name]="'person'"
                        ></tapestry-icon>
                        <span class="tapestry-body body-sm">
                            {{
                                'label.common-terms.myprofile' | translate
                            }}</span
                        >
                    </mat-panel-title>
                </mat-expansion-panel-header>
                <my-profile
                    class="mobile-profile"
                    [isMobileMenu]="true"
                ></my-profile>
            </mat-expansion-panel>
            @for (item of mobileMenuItems; track item) {
                <button
                    mat-menu-item
                    (click)="onSelect(item.id)"
                    class="mobile-menu-items"
                    [attr.data-tracking]="item.tracking"
                >
                    @if (item.icon) {
                        <tapestry-icon
                            class="mobile-menu-item--icon"
                            [name]="item.icon"
                        ></tapestry-icon>
                    }
                    <span class="tapestry-body body-sm">{{ item.title }}</span>
                </button>
            }
        </mat-menu>
        <mat-menu #myProfile="matMenu" class="my-profile-dropdown">
            <my-profile></my-profile>
        </mat-menu>
    `,
    styleUrls: ['./mobile-nav.component.less'],
    standalone: false
})
export class MobileNavComponent {
    @Input() modules: IModule[] = [];
    @Output() showMyProfile: EventEmitter<void> = new EventEmitter<void>();

    mobileMenuItems: IMobileMenuItem[] = [];

    constructor(
        public TR: TranslationService,
        private readonly navService: GlobalNavService,
        private readonly router: Router,
        private readonly moduleAccess: ModuleAccessService,
        private readonly featureFlag: FeatureFlagsService,
        private readonly moduleUserAccess: ModuleUserAccessService
    ) {
        this.mobileMenuItems = this.getMobileMenuItems();
    }

    onSelect(id: string) {
        void this.navService.menuAction(id as GlobalNavMenuActions);
    }

    getMobileMenuItems(): IMobileMenuItem[] {
        const sellerToolsEnabled =
            this.featureFlag.getByFlag(
                FeatureFlags.enableInternalOpenInvitation
            ) ||
            this.moduleAccess.accountClassificationType ===
                ClassificationType.Seller;

        const globalAdminEnabled = this.userIsAdmin();

        const groupAnalyticsEnabled =
            !this.featureFlag.getByFlag(FeatureFlags.hideGroupAnalyticsNav) ||
            this.userIsAdmin();

        return [
            ...(globalAdminEnabled
                ? ([
                      {
                          id: GlobalNavMenuActions.Administration,
                          title: this.TR.key('label.admin.administration'),
                          icon: 'gear',
                          tracking: 'mobile-admin-button'
                      }
                  ] as IMobileMenuItem[])
                : []),
            ...(groupAnalyticsEnabled
                ? ([
                      {
                          id: GlobalNavMenuActions.MobileGroupAnalytics,
                          title: this.TR.key('analytics.title'),
                          icon: 'graph_analytics',
                          tracking: 'mobile-group-analytics-button'
                      }
                  ] as IMobileMenuItem[])
                : []),
            ...(sellerToolsEnabled
                ? ([
                      {
                          id: GlobalNavMenuActions.MobileSellerTools,
                          title: this.TR.key(
                              'client-core.global-nav.partner-support.title'
                          ),
                          icon: 'pattern',
                          tracking: 'mobile-seller-tools-button'
                      }
                  ] as IMobileMenuItem[])
                : []),
            {
                id: GlobalNavMenuActions.MobileSignout,
                title: this.TR.key('label.common-terms.logout'),
                icon: 'close_panel',
                tracking: 'mobile-signout-button'
            }
        ];
    }

    isActiveModule(moduleName: string) {
        return this.router.url.includes(moduleName?.toLowerCase());
    }

    userIsAdmin(): boolean {
        return this.moduleUserAccess.hasAccessLevelForAnyModule(
            PermissionsModuleAccessLevel.AllModuleAdmin
        );
    }
}
